import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import ClientDetailsModal from './components/ClientDetailsModal';
import {
  clientService,
  type Client,
  type ClientStatus,
} from '../../services/clientService';
import {
  adminSubscriptionService,
  type AdminSubscription,
  type SubscriptionBillingStatus,
  type SubscriptionInterval,
  type SubscriptionStatus,
} from '../../services/adminSubscriptionService';
import {
  adminOrderService,
  type AdminOrder,
} from '../../services/adminOrderService';
import { getAllServices, type Service } from '../../services/servicesStore';
import { currencyService } from '../../services/currencyService';

const STATUS_OPTIONS: Array<{ value: 'all' | ClientStatus; label: string }> = [
  { value: 'all', label: 'Tous' },
  { value: 'pending', label: 'En attente' },
  { value: 'active', label: 'En cours' },
  { value: 'completed', label: 'Termines' },
];

const STATUS_LABELS: Record<ClientStatus, string> = {
  pending: 'En attente',
  active: 'En cours',
  completed: 'Termine',
};

const STATUS_BADGES: Record<ClientStatus, string> = {
  pending: 'bg-amber-100 text-amber-700',
  active: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
};

const SOURCE_LABELS = {
  manual: 'Manuel',
  payment: 'Paiement',
};

const BILLING_TYPE_BADGES = {
  subscription: 'bg-indigo-100 text-indigo-700',
  one_time: 'bg-slate-100 text-slate-700',
};

const BILLING_TYPE_LABELS = {
  subscription: 'Abonnement',
  one_time: 'Paiement unique',
};

const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  trial: 'Essai',
  active: 'Actif',
  paused: 'En pause',
  past_due: 'En retard',
  canceled: 'Annule',
  expired: 'Expire',
};

const BILLING_STATUS_LABELS: Record<SubscriptionBillingStatus, string> = {
  scheduled: 'Planifie',
  paid: 'Paye',
  overdue: 'En retard',
  unpaid: 'Impayee',
};

const INTERVAL_LABELS: Record<SubscriptionInterval, string> = {
  monthly: 'Mensuel',
  quarterly: 'Trimestriel',
  yearly: 'Annuel',
};

const SUBSCRIPTION_PRIORITY: Record<SubscriptionStatus, number> = {
  active: 0,
  trial: 1,
  past_due: 2,
  paused: 3,
  canceled: 4,
  expired: 5,
};

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

const ClientManager = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [subscriptions, setSubscriptions] = useState<AdminSubscription[]>([]);
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [displayCountry, setDisplayCountry] = useState(currencyService.getCountry());
  const [isLoading, setIsLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ClientStatus>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [modalMode, setModalMode] = useState<'view' | 'create'>('view');
  const [isSyncingSupabase, setIsSyncingSupabase] = useState(false);

  const loadData = async () => {
    setIsLoading(true);

    try {
      const [clientsResult, subscriptionsResult, ordersResult, servicesResult] =
        await Promise.allSettled([
          clientService.getClients(true),
          adminSubscriptionService.getSubscriptions(true),
          adminOrderService.getOrders(true),
          getAllServices(),
        ]);

      if (clientsResult.status === 'fulfilled') {
        setClients(clientsResult.value);
      } else {
        console.error(clientsResult.reason);
        setClients([]);
      }

      if (subscriptionsResult.status === 'fulfilled') {
        setSubscriptions(subscriptionsResult.value);
      } else {
        console.error(subscriptionsResult.reason);
        setSubscriptions([]);
      }

      if (ordersResult.status === 'fulfilled') {
        setOrders(ordersResult.value);
      } else {
        console.error(ordersResult.reason);
        setOrders([]);
      }

      if (servicesResult.status === 'fulfilled') {
        setServices(servicesResult.value);
      } else {
        console.error(servicesResult.reason);
        setServices([]);
      }
    } catch (error) {
      console.error(error);
      toast.error(error instanceof Error ? error.message : 'Erreur de chargement des clients');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData().catch((error) => {
      console.error(error);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    return currencyService.subscribe((nextCountry) => {
      setDisplayCountry(nextCountry);
    });
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const displayCurrencyCode = currencyService.getDisplayCurrencyCode(displayCountry);

  const formatOriginalAmount = (amount: number, currencyCode: string) =>
    currencyService.formatCurrencyAmount(amount, currencyCode, {
      display: 'code',
    });

  const formatDisplayAmount = (amount: number, currencyCode: string) =>
    currencyService.formatAmountForDisplay(amount, currencyCode, {
      country: displayCountry,
      display: 'code',
    });

  const filteredClients = clients.filter((client) => {
    const matchesStatus =
      statusFilter === 'all' ? true : client.status === statusFilter;

    const matchesQuery =
      !normalizedQuery ||
      [
        client.name,
        client.email,
        client.company,
        client.companyNumber,
        client.address,
        client.phone,
        client.serviceName,
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(normalizedQuery));

    return matchesStatus && matchesQuery;
  });

  const metrics = clients.reduce(
    (accumulator, client) => {
      accumulator.total += 1;
      accumulator.revenue += currencyService.convertToDisplayAmount(
        client.totalAmount || 0,
        client.currencyCode || 'EUR',
        displayCountry
      );

      if (client.status === 'pending') {
        accumulator.pending += 1;
      }

      if (client.status === 'active') {
        accumulator.active += 1;
      }

      if (client.status === 'completed') {
        accumulator.completed += 1;
      }

      return accumulator;
    },
    {
      total: 0,
      pending: 0,
      active: 0,
      completed: 0,
      revenue: 0,
    }
  );

  const handleOpenCreate = () => {
    const emptyClient = clientService.createEmptyClient();
    const firstService = services[0];

    setSelectedClient({
      ...emptyClient,
      serviceId: firstService?.id || '',
      serviceName: firstService?.name || '',
    });
    setModalMode('create');
  };

  const handleOpenView = (client: Client) => {
    setSelectedClient(client);
    setModalMode('view');
  };

  const handleSave = async (client: Client) => {
    try {
      await clientService.saveClient(client);
      await loadData();
      setSelectedClient(null);
      toast.success(
        modalMode === 'create' ? 'Client cree avec succes !' : 'Client mis a jour !'
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? `Impossible de sauvegarder ce client: ${error.message}`
          : 'Impossible de sauvegarder ce client'
      );
    }
  };

  const handleDelete = async (client: Client) => {
    if (!window.confirm(`Supprimer ${client.name} ?`)) {
      return;
    }

    try {
      await clientService.removeClient(client.id);
      await loadData();
      setSelectedClient(null);
      toast.success('Client supprime !');
    } catch (error) {
      console.error(error);
      toast.error('Impossible de supprimer ce client');
    }
  };

  const handleSyncClientsFromRecords = async () => {
    setIsSyncingSupabase(true);
    try {
      const [ordersData, subscriptionsData, currentClients] = await Promise.all([
        adminOrderService.getOrders(true),
        adminSubscriptionService.getSubscriptions(true),
        clientService.getClients(true),
      ]);

      const existingEmails = new Set(
        currentClients.map((client) => (client.email || '').trim().toLowerCase()).filter(Boolean)
      );
      const pendingUpserts: Client[] = [];

      const pushIfNewEmail = (candidate: {
        name: string;
        email: string;
        phone?: string;
        company?: string;
        companyNumber?: string;
        address?: string;
        serviceId?: string;
        serviceName?: string;
        amount?: number;
        currencyCode?: string;
        purchaseDate?: string;
      }) => {
        const normalizedEmail = (candidate.email || '').trim().toLowerCase();
        if (!normalizedEmail || existingEmails.has(normalizedEmail)) {
          return;
        }

        existingEmails.add(normalizedEmail);
        pendingUpserts.push({
          id: clientService.createEmptyClient().id,
          name: candidate.name || normalizedEmail,
          email: normalizedEmail,
          phone: candidate.phone || '',
          company: candidate.company || '',
          companyNumber: candidate.companyNumber || '',
          address: candidate.address || '',
          purchaseDate: candidate.purchaseDate || new Date().toISOString(),
          serviceId: candidate.serviceId || '',
          serviceName: candidate.serviceName || '',
          status: 'active',
          notes: 'Import automatique depuis commandes/abonnements admin.',
          totalAmount: Number(candidate.amount || 0),
          currencyCode: candidate.currencyCode || 'EUR',
          source: 'manual',
          lastContactAt: '',
        });
      };

      ordersData.forEach((order) => {
        pushIfNewEmail({
          name: order.clientName,
          email: order.clientEmail,
          phone: order.clientPhone,
          company: order.clientCompany,
          serviceId: order.serviceId,
          serviceName: order.serviceName,
          amount: order.amount,
          currencyCode: order.currencyCode,
          purchaseDate: order.orderedAt || order.lastUpdateAt,
        });
      });

      subscriptionsData.forEach((subscription) => {
        pushIfNewEmail({
          name: subscription.clientName,
          email: subscription.clientEmail,
          phone: subscription.clientPhone,
          company: subscription.clientCompany,
          serviceId: subscription.serviceId,
          serviceName: subscription.serviceName,
          amount: subscription.amount,
          currencyCode: subscription.currencyCode,
          purchaseDate: subscription.startDate || subscription.updatedAt,
        });
      });

      await Promise.all(pendingUpserts.map((client) => clientService.saveClient(client)));
      await loadData();
      toast.success(
        pendingUpserts.length > 0
          ? `${pendingUpserts.length} client(s) importé(s) vers Supabase.`
          : 'Supabase déjà synchronisé: aucun nouveau client à importer.'
      );
    } catch (error) {
      console.error(error);
      toast.error(
        error instanceof Error
          ? `Sync Supabase impossible: ${error.message}`
          : 'Sync Supabase impossible.'
      );
    } finally {
      setIsSyncingSupabase(false);
    }
  };

  const normalizeEmail = (value?: string) => (value || '').trim().toLowerCase();

  const getClientSubscriptions = (client: Client) =>
    subscriptions
      .filter(
        (subscription) =>
          subscription.clientId === client.id ||
          normalizeEmail(subscription.clientEmail) === normalizeEmail(client.email)
      )
      .sort((left, right) => {
        const statusDelta =
          (SUBSCRIPTION_PRIORITY[left.status] ?? 99) -
          (SUBSCRIPTION_PRIORITY[right.status] ?? 99);

        if (statusDelta !== 0) {
          return statusDelta;
        }

        const leftTime = left.nextBillingDate
          ? new Date(left.nextBillingDate).getTime()
          : Number.MAX_SAFE_INTEGER;
        const rightTime = right.nextBillingDate
          ? new Date(right.nextBillingDate).getTime()
          : Number.MAX_SAFE_INTEGER;

        return leftTime - rightTime;
      });

  const getClientOrders = (client: Client) =>
    orders
      .filter(
        (order) =>
          order.clientId === client.id ||
          normalizeEmail(order.clientEmail) === normalizeEmail(client.email)
      )
      .sort(
        (left, right) =>
          new Date(right.orderedAt || right.lastUpdateAt).getTime() -
          new Date(left.orderedAt || left.lastUpdateAt).getTime()
      );

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Clients</h2>
          <p className="text-gray-600 mt-2">
            Centralise les clients, les achats, le suivi commercial et les dossiers
            ouverts.
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Affichage admin actuel: {displayCurrencyCode}. Les montants d&apos;origine
            restent conservés par client.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSyncClientsFromRecords}
            disabled={isSyncingSupabase}
            className="inline-flex items-center justify-center gap-2 bg-gray-100 text-gray-700 px-5 py-3 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-60"
          >
            {isSyncingSupabase ? 'Sync Supabase...' : 'Sync clients Supabase'}
          </button>
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center justify-center gap-2 bg-neon-orange text-white px-5 py-3 rounded-lg hover:bg-[#FF4500] transition-colors"
          >
            <span className="text-lg leading-none">+</span>
            <span>Nouveau client</span>
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 mb-8">
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Clients en base</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{metrics.total}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">Dossiers en cours</div>
          <div className="text-3xl font-bold text-blue-600 mt-2">{metrics.active}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">A relancer</div>
          <div className="text-3xl font-bold text-amber-600 mt-2">{metrics.pending}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
          <div className="text-sm text-gray-500">CA catalogue suivi ({displayCurrencyCode})</div>
          <div className="text-3xl font-bold text-emerald-600 mt-2">
            {currencyService.formatCurrencyAmount(metrics.revenue, displayCurrencyCode, {
              country: displayCountry,
              display: 'code',
            })}
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-6">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recherche
            </label>
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Nom, email, entreprise, service..."
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(event.target.value as 'all' | ClientStatus)
              }
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-neon-orange focus:ring-2 focus:ring-neon-orange/20 outline-none bg-white"
            >
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 text-center text-gray-600">Chargement des clients...</div>
      ) : filteredClients.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-12 text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Aucun client pour le moment
          </h3>
          <p className="text-gray-600 mb-6">
            Cree ton premier dossier client pour commencer a structurer l&apos;administration.
          </p>
          <button
            onClick={handleOpenCreate}
            className="bg-neon-orange text-white px-5 py-3 rounded-lg hover:bg-[#FF4500] transition-colors"
          >
            Creer un client
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Client
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Service
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Statut
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Achat
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Facturation
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Prochain paiement
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Source
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredClients.map((client, index) => (
                  (() => {
                    const clientSubscriptions = getClientSubscriptions(client);
                    const primarySubscription = clientSubscriptions[0];
                    const billingType = clientSubscriptions.length > 0 ? 'subscription' : 'one_time';

                    return (
                      <motion.tr
                        key={client.id}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        className="hover:bg-gray-50/80"
                      >
                        <td className="px-6 py-4">
                          <div className="font-semibold text-gray-900">{client.name}</div>
                          <div className="text-sm text-gray-600">{client.email}</div>
                          {client.company && (
                            <div className="text-xs text-gray-500 mt-1">{client.company}</div>
                          )}
                          {client.companyNumber && (
                            <div className="text-xs text-gray-500 mt-1">
                              No entreprise: {client.companyNumber}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-gray-900">{client.serviceName || 'Non attribue'}</div>
                          {client.totalAmount > 0 && (
                            <div className="text-sm text-gray-500">
                              {formatDisplayAmount(client.totalAmount, client.currencyCode)}
                              {client.currencyCode !== displayCurrencyCode && (
                                <span className="mt-1 block text-xs text-gray-400">
                                  Origine: {formatOriginalAmount(client.totalAmount, client.currencyCode)}
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${STATUS_BADGES[client.status]}`}
                          >
                            {STATUS_LABELS[client.status]}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {formatDate(client.purchaseDate)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-2">
                            <span
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${BILLING_TYPE_BADGES[billingType]}`}
                            >
                              {BILLING_TYPE_LABELS[billingType]}
                            </span>
                            {primarySubscription && (
                              <div className="text-xs text-gray-500">
                                {INTERVAL_LABELS[primarySubscription.interval]} • {SUBSCRIPTION_STATUS_LABELS[primarySubscription.status]}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {primarySubscription?.nextBillingDate ? (
                            <div>
                              <div>{formatDate(primarySubscription.nextBillingDate)}</div>
                              <div className="text-xs text-gray-400 mt-1">
                                {BILLING_STATUS_LABELS[primarySubscription.billingStatus]}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">Aucune</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {SOURCE_LABELS[client.source]}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => handleOpenView(client)}
                              className="px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                            >
                              Voir
                            </button>
                            <button
                              onClick={() => handleDelete(client)}
                              className="px-3 py-2 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                            >
                              Supprimer
                            </button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })()
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedClient && (
        <ClientDetailsModal
          client={selectedClient}
          services={services}
          relatedSubscriptions={getClientSubscriptions(selectedClient)}
          relatedOrders={getClientOrders(selectedClient)}
          defaultMode={modalMode}
          onClose={() => setSelectedClient(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default ClientManager;
